import type React from "react"

import {
  BarChart3,
  BookText,
  CheckSquare,
  FileSpreadsheet,
  FileText,
  LayoutList,
  ListTree,
  Mail,
  Map,
  ScanSearch,
  Table,
} from "@/components/icons"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"

export type DeliverableType =
  | "research_report"
  | "memo"
  | "comparison_table"
  | "howto_guide"
  | "checklist"
  | "analysis"
  | "timeline"
  | "audit"
  | "data_export"
  | "diagram"
  | "freeform"

interface DeliverableFormatInfo {
  labelKey: string
  icon: React.ReactNode
  accentClass: string
  showToc: boolean
  showStepCounter: boolean
}

export const FORMAT_INFO: Record<DeliverableType, DeliverableFormatInfo> = {
  research_report: {
    labelKey: "deepExplorer.deliverable_formats.research_report",
    icon: <BookText className="size-3.5" />,
    accentClass: CATEGORY_TONES[0],
    showToc: true,
    showStepCounter: false,
  },
  memo: {
    labelKey: "deepExplorer.deliverable_formats.memo",
    icon: <Mail className="size-3.5" />,
    accentClass: CATEGORY_TONES[1],
    showToc: false,
    showStepCounter: false,
  },
  comparison_table: {
    labelKey: "deepExplorer.deliverable_formats.comparison_table",
    icon: <Table className="size-3.5" />,
    accentClass: CATEGORY_TONES[2],
    showToc: false,
    showStepCounter: false,
  },
  howto_guide: {
    labelKey: "deepExplorer.deliverable_formats.howto_guide",
    icon: <LayoutList className="size-3.5" />,
    accentClass: CATEGORY_TONES[3],
    showToc: true,
    showStepCounter: true,
  },
  checklist: {
    labelKey: "deepExplorer.deliverable_formats.checklist",
    icon: <CheckSquare className="size-3.5" />,
    accentClass: CATEGORY_TONES[4],
    showToc: false,
    showStepCounter: false,
  },
  analysis: {
    labelKey: "deepExplorer.deliverable_formats.analysis",
    icon: <BarChart3 className="size-3.5" />,
    accentClass: CATEGORY_TONES[0],
    showToc: true,
    showStepCounter: false,
  },
  timeline: {
    labelKey: "deepExplorer.deliverable_formats.timeline",
    icon: <Map className="size-3.5" />,
    accentClass: CATEGORY_TONES[1],
    showToc: false,
    showStepCounter: false,
  },
  audit: {
    labelKey: "deepExplorer.deliverable_formats.audit",
    icon: <ScanSearch className="size-3.5" />,
    accentClass: CATEGORY_TONES[2],
    showToc: true,
    showStepCounter: false,
  },
  data_export: {
    labelKey: "deepExplorer.deliverable_formats.data_export",
    icon: <FileSpreadsheet className="size-3.5" />,
    accentClass: CATEGORY_TONES[3],
    showToc: false,
    showStepCounter: false,
  },
  diagram: {
    labelKey: "deepExplorer.deliverable_formats.diagram",
    icon: <ListTree className="size-3.5" />,
    accentClass: CATEGORY_TONES[4],
    showToc: false,
    showStepCounter: false,
  },
  freeform: {
    labelKey: "deepExplorer.deliverable_formats.freeform",
    icon: <FileText className="size-3.5" />,
    // Freeform has no imposed shape, so its badge stays neutral.
    accentClass: "bg-muted text-muted-foreground border border-border",
    showToc: false,
    showStepCounter: false,
  },
}

// Mirrors the backend's DEFAULT_FORMAT. When parseDeliverableType returns null
// and the caller didn't supply an explicit deliverableType, the renderer is
// looking at content with no declared shape — which is exactly the case
// freeform was designed to handle. The chrome is neutral, so falling back to
// it can't visually misrepresent the content the way falling back to
// research_report (with its blue book badge + ToC) would.
export const DEFAULT_FORMAT: DeliverableType = "freeform"

const DELIVERABLE_TYPE_COMMENT_RE = /<!--\s*deliverable_type\s*:\s*([a-z_]+)\s*-->/i

/** Extract the deliverable type from an HTML comment in the file content. */
export function parseDeliverableType(content: string): DeliverableType | null {
  const match = DELIVERABLE_TYPE_COMMENT_RE.exec(content)
  if (!match) return null
  const key = match[1].toLowerCase() as DeliverableType
  return key in FORMAT_INFO ? key : null
}
